import { Button, ButtonWithIcon } from '@/components';
import {
    AttachmentIcon,
    AuditIcon,
    TrashIcon,
    VideoIcon,
} from '@/components/Icons';
import { R } from '@/features/booking/resources/Texts.resx';
import { useAttachments } from '@/features/booking/widgets/describe-problem/model/useAttachments';
import { FileRejections } from '@/features/booking/widgets/describe-problem/ui/FileRejections';

type AttachmentsDropzoneProps = {
    onChange: (uids: string[], uploadInProgress: boolean) => void;
    className?: string;
    previewOnly?: boolean;
};

export const AttachmentsDropzone = ({
    onChange,
    className,
    previewOnly = false,
}: AttachmentsDropzoneProps) => {
    const {
        files,
        fileRejections,
        getRootProps,
        getInputProps,
        removeFile,
        handleButtonClick,
        fileInFocus,
        setFileInFocus,
    } = useAttachments({ onChange });

    const previewWidth = files.length > 8 ? 'w-3/17' : 'w-24';

    return (
        <div className={className}>
            {!previewOnly && (
                <>
                    <div className="flex flex-row justify-center">
                        {files.length < 10 && (
                            <ButtonWithIcon
                                variant="ghost"
                                icon={<AttachmentIcon />}
                                onClick={handleButtonClick}
                                disabled={files.some((f) => f.progress < 100)}
                            >
                                {R.AttachFiles}
                            </ButtonWithIcon>
                        )}

                        {files.length >= 10 && (
                            <Button variant="ghost" disabled={true}>
                                {R.MaxUploadsReached}
                            </Button>
                        )}
                    </div>

                    {!!fileRejections.length && (
                        <FileRejections fileRejections={fileRejections} />
                    )}

                    {/* Hidden dropzone input */}
                    <div {...getRootProps()} style={{ display: 'none' }}>
                        <input {...getInputProps()} />
                    </div>
                </>
            )}

            {/* Previews */}
            {files.length > 0 && (
                <div
                    className={`my-4 grid ${files.length <= 8 ? 'grid-cols-4' : 'grid-cols-5'} gap-1`}
                >
                    {files.map((file) => {
                        const isFocused = file.uid === fileInFocus;
                        const isVideo =
                            file.name.endsWith('.mov') ||
                            file.name.endsWith('.mp4');
                        const isPdf = file.name.endsWith('.pdf');
                        const isImage = !isVideo && !isPdf;
                        const isUploading = file.progress < 100;

                        return (
                            <div
                                key={file.uid ?? file.name}
                                className={`w-full cursor-pointer rounded-xl border border-gray-300 text-center ${previewWidth} ${file.progress === 100 ? 'overflow-visible' : 'overflow-hidden'} `}
                                onMouseEnter={() => setFileInFocus(file.uid)}
                                onMouseLeave={() => setFileInFocus(null)}
                                onTouchStart={() => setFileInFocus(file.uid)}
                                onTouchEnd={() => setFileInFocus(null)}
                            >
                                {/* Preview */}
                                <div className="relative h-20 w-full">
                                    {file.previewUrl && isImage && (
                                        <img
                                            src={file.previewUrl}
                                            alt={file.name}
                                            className={`h-full w-full object-cover transition-all duration-300 ${isUploading ? 'blur-sm' : 'blur-0'} ${previewOnly ? 'rounded-xl' : 'rounded-t-xl'} `}
                                        />
                                    )}

                                    {!isImage && (
                                        <div
                                            className={`relative flex h-full w-full items-center justify-center overflow-hidden rounded-t-xl bg-gray-300 object-cover transition-all duration-300 ${isUploading ? 'animate-pulse blur-sm' : 'blur-0'} `}
                                        >
                                            {isVideo && <VideoIcon />}
                                            {isPdf && <AuditIcon />}
                                        </div>
                                    )}

                                    {/* Filename overlay */}
                                    <div
                                        className={`absolute top-1/2 left-1/2 z-50 -translate-x-1/2 -translate-y-1/2 transform transition-opacity duration-300 ${isFocused ? 'opacity-100' : 'opacity-0'}`}
                                        style={{
                                            maxWidth: '300px',
                                            minWidth: '150px',
                                            whiteSpace: 'normal',
                                        }}
                                    >
                                        <div className="bg-opacity-80 line-clamp-3 max-h-20 rounded bg-gray-900 px-2 py-1 text-center text-sm wrap-break-word text-ellipsis text-white">
                                            {file.name}
                                        </div>
                                    </div>
                                </div>

                                {/* Remove button */}
                                {!previewOnly && (
                                    <div className="flex flex-row items-center justify-center">
                                        {isUploading && (
                                            <div
                                                className="bg-primary-blue h-5 rounded-xl p-2"
                                                style={{
                                                    width: `${file.progress + 1}%`,
                                                }}
                                            />
                                        )}

                                        {!isUploading && (
                                            <button
                                                type="button"
                                                className="cursor-pointer p-1 hover:scale-120"
                                                onClick={() =>
                                                    removeFile(file.uid)
                                                }
                                            >
                                                <TrashIcon
                                                    width={20}
                                                    height={20}
                                                />
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};
